<script setup lang="ts" generic="T = Property">
import type { StandardSliderEmits, StandardSliderProps } from '.';
import type Carousel from '../ui/carousel/Carousel.vue';
import type { Property } from '~/core/entities/property';

import { cn } from '@/utils';
import { WheelGesturesPlugin } from 'embla-carousel-wheel-gestures';
import { Primitive } from 'reka-ui';

import { carouselItemVariant } from '.';
import {
	StandardSliderArrowNext,
	StandardSliderArrowPrev
} from './StandardSliderArrows';

const props = withDefaults(defineProps<StandardSliderProps<T>>(), {
	containerClass: '',
	mainContainerClass: '',
	footerClass: '',
	buttonVariant: 'primary',
	buttonClass: '',
	buttonSkeletonClass: '',
	sectionName: 'listed properties',
	isLoading: true,
	title: 'Explore imóveis selecionados',
	viewAllText: 'Ver todos',
	carouselItemSize: 'default',
	propertyCardVariant: 'whiteBackground',
	slidesToScroll: 1,
	titleAs: 'h2',
	titleClasses: '',
	carouselContentClass: '',
	selectorVariant: 'dark',
	selectorArrowsVariant: 'dark',
	showNavigationArrows: false,
	alwaysShowArrows: false,
	displayTitle: true,
	displayFooter: true,
	limitPhotos: true
});

const emit = defineEmits<StandardSliderEmits>();

const carouselContainerRef = ref<InstanceType<typeof Carousel> | null>(null);
const carouselItemSelectorContainerRef = ref<HTMLDivElement | null>(null);
const isMouseOverCarousel = ref(false);

function isPropertyItem(item: any): item is Property {
	return (
		item && typeof item === 'object' && 'commercialId' in item && 'slug' in item
	);
}

const {
	currentCarouselItemIndex,
	scrollableSlidesNumber,
	carouselDynamicItemWidth,
	scrollTo,
	reinitializeCarousel
} = useCarouselControl({
	carouselContainerRef,
	carouselItemSelectorContainerRef,
	cyclic: props.carouselOptions?.cyclic ?? true
});

// Navigation arrow controls
const canScrollPrev = computed(() => {
	return carouselContainerRef.value?.canScrollPrev ?? false;
});

const canScrollNext = computed(() => {
	return carouselContainerRef.value?.canScrollNext ?? false;
});

function scrollNext() {
	carouselContainerRef.value?.scrollNext();
}

function scrollPrev() {
	carouselContainerRef.value?.scrollPrev();
}

const showSliderSelector = computed<boolean>(
	() => scrollableSlidesNumber.value > 1
);

function handleViewMoreClicked(href: string) {
	emit('cta-view-more-clicked', {
		destination_url: href,
		cta_text: props.viewAllText
	});
}

const getSkeletonClass = computed<string>(() =>
	props.isLoading ? 'h-7 w-1/2 md:w-[359px]' : ''
);

const limitedItems = computed(() => {
	if (!props.items) return [];

	if (!props.limitPhotos) return props.items.filter(Boolean);

	return props.items.slice(0, 25).filter(Boolean);
});

const isLoadingAndNoItems = computed(
	() => props.isLoading && !limitedItems.value.length
);

const hasInitializedItems = ref(false);

watchEffect(async () => {
	if (props.items?.length) {
		await until(() => carouselContainerRef.value?.carouselApi).toBeTruthy();
		nextTick(() => {
			reinitializeCarousel?.();
			if (!hasInitializedItems.value) {
				scrollTo(0, true);
				hasInitializedItems.value = true;
			}
		});
	}
});

const isPrevVisible = computed(() => {
	return props.alwaysShowArrows
		? canScrollPrev.value
		: isMouseOverCarousel.value && canScrollPrev.value;
});

const isNextVisible = computed(() => {
	return props.alwaysShowArrows
		? canScrollNext.value
		: isMouseOverCarousel.value && canScrollNext.value;
});

function setSelectorContainerRef(el: Element | ComponentPublicInstance | null) {
	carouselItemSelectorContainerRef.value = (el as HTMLDivElement) ?? null;
}

// Expose reactive properties for parent components
defineExpose({
	currentCarouselItemIndex,
	scrollableSlidesNumber,
	scrollTo,
	reinitializeCarousel
});
</script>

<template>
	<div class="w-full overflow-hidden">
		<Container
			:class="cn(' flex-1 w-full bg-white', props.containerClass)"
			:main-class="cn(props.mainContainerClass)"
		>
			<slot v-if="displayTitle" name="header">
				<header class="flex flex-col items-start gap-6 pb-6 md:pb-9">
					<div class="flex w-full flex-col items-start justify-between">
						<slot name="title">
							<Primitive
								:as="titleAs"
								:class="
									cn(
										'flex items-center font-matter-sq text-2xl font-normal leading-[150%] tracking-[-0.03rem] text-primary md:text-4xl md:tracking-[-0.045rem]',
										titleClasses
									)
								"
							>
								{{ title }}
							</Primitive>
						</slot>
					</div>
					<slot name="navigation"></slot>
				</header>
			</slot>

			<div
				:class="
					cn('flex w-full flex-col gap-6 md:gap-9', carouselOptions?.class)
				"
			>
				<div
					:class="cn('relative', carouselOptions?.carouselWrapperClasses)"
					@mouseenter="isMouseOverCarousel = true"
					@mouseleave="isMouseOverCarousel = false"
				>
					<Carousel
						ref="carouselContainerRef"
						:class="cn(carouselOptions?.class, 'w-full')"
						:plugins="[WheelGesturesPlugin()]"
						:opts="{ align: 'start', slidesToScroll, breakpoints }"
					>
						<CarouselContent
							:class="cn('gap-5 lg:gap-[18px]', carouselContentClass)"
							main-class="!overflow-visible md:!overflow-hidden"
						>
							<template v-if="isLoading">
								<CarouselItem
									v-for="index in 6"
									:key="`item-skeleton-${index}`"
									:class="
										cn(
											carouselItemVariant({ size: carouselItemSize }),
											carouselItemClass
										)
									"
								>
									<slot name="skeleton">
										<PropertyCardSkeleton
											:key="`item-skeleton-${index}`"
											:variant="propertyCardVariant"
											size="sm"
										/>
									</slot>
								</CarouselItem>
							</template>

							<template v-else-if="limitedItems.length">
								<CarouselItem
									v-for="(item, index) in limitedItems"
									:key="
										(item as any)?.commercialId ?? (item as any)?.id ?? index
									"
									:class="
										cn(
											carouselItemVariant({ size: carouselItemSize }),
											props.carouselItemClass
										)
									"
								>
									<slot name="items" :item="item" :index="index">
										<template v-if="isPropertyItem(item)">
											<PropertyCard
												:variant="propertyCardVariant"
												:section="sectionName"
												:property="item"
												:card-position="index"
												:tracking-context="trackingContext"
												:position-in-list="index"
											/>
										</template>
									</slot>
								</CarouselItem>
								<slot name="default-items"></slot>
							</template>
						</CarouselContent>
					</Carousel>

					<!-- Navigation Arrows -->
					<template v-if="showNavigationArrows">
						<StandardSliderArrowPrev
							:disabled="!canScrollPrev"
							:is-visible="isPrevVisible"
							@click="scrollPrev"
						/>
						<StandardSliderArrowNext
							:disabled="!canScrollNext"
							:is-visible="isNextVisible"
							@click="scrollNext"
						/>
					</template>
				</div>

				<slot
					v-if="displayFooter"
					name="footer"
					:current-index="currentCarouselItemIndex"
					:total-slides="scrollableSlidesNumber"
					:scroll-to="scrollTo"
					:dynamic-item-width="carouselDynamicItemWidth"
					:set-selector-container-ref="setSelectorContainerRef"
				>
					<div
						:class="
							cn(
								'flex h-auto w-full flex-col-reverse items-start justify-between gap-6 md:flex-row lg:items-center',
								footerClass
							)
						"
					>
						<slot name="cta">
							<NuxtLink
								v-if="viewAllHref"
								v-slot="{ href }"
								:to="viewAllHref"
								custom
								@click="handleViewMoreClicked(viewAllHref)"
							>
								<Button
									as="a"
									:href="href"
									:variant="buttonVariant"
									:class="
										cn(
											'w-min self-start px-4 py-3 transition hover:decoration-primary md:px-5 md:py-[11px]',
											buttonClass
										)
									"
									:is-skeleton-loading="isLoadingAndNoItems"
									:skeleton-classes="
										cn('w-[146px] h-[52px]', buttonSkeletonClass)
									"
									skeleton-variant="white"
								>
									{{ viewAllText }}
									<IconMaterialSymbolsNorthEast class="w-4 md:w-5" />
								</Button>
							</NuxtLink>
						</slot>

						<Skeleton
							:is-active="isLoadingAndNoItems"
							variant="white"
							:class="cn(getSkeletonClass, 'w-full')"
						>
							<slot
								name="controls"
								:current-carousel-item-index="currentCarouselItemIndex"
								:scrollable-slides-number="scrollableSlidesNumber"
								:scroll-to="scrollTo"
							>
								<div
									v-if="showSliderSelector"
									class="flex w-full items-center justify-end gap-3"
								>
									<div
										ref="carouselItemSelectorContainerRef"
										class="flex w-full flex-wrap justify-end gap-2 md:max-w-[420px] md:gap-3"
									>
										<CarouselItemSelector
											v-for="(_, index) in scrollableSlidesNumber"
											:key="`selector-${index}`"
											:active="currentCarouselItemIndex === index"
											:delay="2000"
											:dynamic-width="carouselDynamicItemWidth"
											:variant="selectorVariant"
											@click="scrollTo(index)"
										/>
									</div>
									<div class="flex max-w-[66px] gap-1 md:gap-2">
										<CarouselGenericAction
											:variant="selectorArrowsVariant"
											@click="scrollTo(currentCarouselItemIndex - 1)"
										>
											<IconMaterialSymbolsLightChevronLeft
												width="32px"
												height="32px"
											/>
										</CarouselGenericAction>
										<CarouselGenericAction
											:variant="selectorArrowsVariant"
											@click="scrollTo(currentCarouselItemIndex + 1)"
										>
											<IconMaterialSymbolsLightChevronRight
												width="32px"
												height="32px"
											/>
										</CarouselGenericAction>
									</div>
								</div>
							</slot>
						</Skeleton>
					</div>
				</slot>
			</div>
		</Container>
	</div>
</template>
